import ComponentHeader from '@atoms/ComponentHeader/ComponentHeader';
import LoadingSpinner from '@atoms/LoadingSpinner/LoadingSpinner';
import { ICameraImage } from '@models/ICameraImage';
import { ILink } from '@models/ILink';
import { IVideo } from '@models/IVideo';
import BreakpointTypeEnum from '@models/enums/BreakpointTypeEnum';
import ImageLightBoxTypeEnum from '@models/enums/ImageLightBoxTypeEnum';
import Carousel from '@molecules/Carousel/Carousel';
import ImageLightBox from '@molecules/ImageLightBox/ImageLightBox';
import InViewVideo from '@molecules/InViewVideo/InViewVideo';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { SwiperSlide } from 'swiper/react';
import { Swiper } from 'swiper/types';

interface Props {
  readonly title?: string;
  readonly subTitle?: string;
  readonly link?: ILink;
  readonly media: ICameraImage[] | IVideo[];
  readonly imageType: ImageLightBoxTypeEnum;
  readonly showYellowTick: boolean;
  readonly anchorId?: string;
}

const ImageCarousel = ({
  title,
  subTitle,
  link,
  media,
  imageType,
  showYellowTick,
  anchorId = '',
}: Props) => {
  const isSmallMobile = useMediaQuery(768);
  const isMobileScreen = useMediaQuery(1024, BreakpointTypeEnum.Screen);
  const [swiperInstance, setSwiperInstance] = useState<Swiper>();
  const [isSwiperBeginning, setIsSwiperBeginning] = useState(true);
  const [isSwiperEnd, setIsSwiperEnd] = useState(false);
  const isSwiperOverflowing = !isSwiperBeginning || !isSwiperEnd;
  const navButtonId = `image-carousel-${anchorId}`;

  const isVideosCarousel = media.length > 0 && media[0] && 'url' in media[0];
  const [videosLoadingStatuses, setVideosLoadingStatuses] = useState<boolean[]>(
    isVideosCarousel ? new Array(media.length).fill(false) : []
  );
  const isEveryVideoLoaded =
    videosLoadingStatuses.length > 0 &&
    videosLoadingStatuses.every((isLoaded) => isLoaded);
  const isLoadingVideos = isVideosCarousel && !isEveryVideoLoaded;

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only run when every video is loaded
  useEffect(() => {
    if (isEveryVideoLoaded && swiperInstance) {
      swiperInstance.slides.forEach((slide) => {
        const videoElementWidth = slide.querySelector('video')?.clientWidth;
        slide.style.width = isSmallMobile ? `${videoElementWidth}px` : '';
      });

      swiperInstance.update();
      swiperInstance.updateSlides();
      setIsSwiperBeginning(swiperInstance.isBeginning);
      setIsSwiperEnd(swiperInstance.isEnd);
    }
  }, [isEveryVideoLoaded, isSmallMobile]);

  return (
    <section
      id={`a-${anchorId}`}
      className="noselect component-padding bg-white text-black-100 dark:bg-black-100 dark:text-white"
      data-testid="CT-44-Image-Carousel-Block"
    >
      <SoftLoadWrapper>
        <div className="flex min-h-[220px] flex-col items-center justify-center overflow-hidden lg:min-h-[400px] lg:gap-10 xl:min-h-[640px]">
          {(title || subTitle || link?.url || !isMobileScreen) && (
            <ComponentHeader
              title={title}
              subtitle={subTitle}
              subtitleClass="text-grey-450 dark:text-grey-500 body-two"
              showYellowTick={showYellowTick}
              cta={link}
              dataTestId="CT-44-Image-Carousel-Component-Header"
              navButtonId={navButtonId}
              hideNavButtons={isLoadingVideos || !isSwiperOverflowing}
              isLeftNavButtonDisabled={isSwiperBeginning}
              isRightNavButtonDisabled={isSwiperEnd}
            />
          )}

          {media?.length > 0 && (
            <div
              className={classNames('image-carousel', {
                'relative w-full': isLoadingVideos,
              })}
            >
              {isLoadingVideos && (
                <div className="absolute inset-0 z-[2] flex items-center justify-center bg-white dark:bg-black-100">
                  <LoadingSpinner />
                </div>
              )}

              <div
                className={classNames(
                  'overflow-swiper container overflow-visible',
                  {
                    'max-md:w-screen': isVideosCarousel,
                  }
                )}
              >
                <Carousel
                  className="w-full"
                  breakpoints={{
                    768: {
                      spaceBetween: 30,
                    },
                  }}
                  observer
                  observeParents
                  onInit={(swiper) => {
                    swiper.update();
                  }}
                  onSetIsSwiperBeginning={setIsSwiperBeginning}
                  onSetIsSwiperEnd={setIsSwiperEnd}
                  onSwiper={setSwiperInstance}
                  slidesPerView="auto"
                  spaceBetween={20}
                  navId={navButtonId}
                >
                  {isVideosCarousel
                    ? (media as IVideo[]).map((video, index) => (
                        <SwiperSlide
                          key={`video-carousel-slide-${video.url}`}
                          className="relative max-md:max-w-[90%]"
                        >
                          <InViewVideo
                            video={video}
                            hasInVideoPlayButton
                            controls={false}
                            dataTestId={`CT-44-Image-Carousel-Video-${index}`}
                            videoClasses="h-[220px] lg:h-[400px] xl:h-[580px] max-md:[&>div>video]:object-cover pointer-events-none"
                            autoPlay
                            videoRatio="aspect-auto"
                            videoWidthStyle="auto"
                            onVideoReady={() => {
                              if (videosLoadingStatuses[index]) return;

                              setVideosLoadingStatuses((prev) => {
                                const prevStatusesCopy = [...prev];
                                prevStatusesCopy[index] = true;
                                return prevStatusesCopy;
                              });
                            }}
                          />
                        </SwiperSlide>
                      ))
                    : (media as ICameraImage[]).map((image, index) => (
                        <SwiperSlide
                          key={`img-carousel-slide-${image.meta?.contentLink}`}
                          className="relative"
                        >
                          <ImageLightBox
                            contentIds={(media as ICameraImage[]).map(
                              ({ meta: imageMeta }) =>
                                imageMeta?.contentLink as string
                            )}
                            imageClasses="h-[180px] md:h-[220px] lg:h-[400px] xl:h-[640px] w-auto"
                            imageType={imageType}
                            {...image}
                            dataTestId={`CT-44-Image-Carousel-Lightbox-${index}`}
                          />
                        </SwiperSlide>
                      ))}
                </Carousel>
              </div>
            </div>
          )}
        </div>
      </SoftLoadWrapper>
    </section>
  );
};

export default ImageCarousel;
